/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { DocumentItemDtoPaging } from '../models/document-item-dto-paging';
import { DocumentVerificationResponseItem } from '../models/document-verification-response-item';
import { downloadProductDocuments } from '../fn/document/download-product-documents';
import { DownloadProductDocuments$Params } from '../fn/document/download-product-documents';
import { downloadSystemDocuments } from '../fn/document/download-system-documents';
import { DownloadSystemDocuments$Params } from '../fn/document/download-system-documents';
import { downloadSystemSpecsDocument } from '../fn/document/download-system-specs-document';
import { DownloadSystemSpecsDocument$Params } from '../fn/document/download-system-specs-document';
import { getDocuments } from '../fn/document/get-documents';
import { GetDocuments$Params } from '../fn/document/get-documents';
import { uploadDocuments } from '../fn/document/upload-documents';
import { UploadDocuments$Params } from '../fn/document/upload-documents';
import { verifyDocuments } from '../fn/document/verify-documents';
import { VerifyDocuments$Params } from '../fn/document/verify-documents';

@Injectable({ providedIn: 'root' })
export class DocumentService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `downloadProductDocuments()` */
  static readonly DownloadProductDocumentsPath = '/business-central/product/{id}/documents';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadProductDocuments()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadProductDocuments$Response(params: DownloadProductDocuments$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return downloadProductDocuments(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `downloadProductDocuments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadProductDocuments(params: DownloadProductDocuments$Params, context?: HttpContext): Observable<Blob> {
    return this.downloadProductDocuments$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `downloadSystemDocuments()` */
  static readonly DownloadSystemDocumentsPath = '/business-central/system/{id}/documents';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadSystemDocuments()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadSystemDocuments$Response(params: DownloadSystemDocuments$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return downloadSystemDocuments(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `downloadSystemDocuments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadSystemDocuments(params: DownloadSystemDocuments$Params, context?: HttpContext): Observable<Blob> {
    return this.downloadSystemDocuments$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `downloadSystemSpecsDocument()` */
  static readonly DownloadSystemSpecsDocumentPath = '/business-central/system/{id}/documents/specs';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadSystemSpecsDocument()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadSystemSpecsDocument$Response(params: DownloadSystemSpecsDocument$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return downloadSystemSpecsDocument(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `downloadSystemSpecsDocument$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadSystemSpecsDocument(params: DownloadSystemSpecsDocument$Params, context?: HttpContext): Observable<Blob> {
    return this.downloadSystemSpecsDocument$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `getDocuments()` */
  static readonly GetDocumentsPath = '/documents';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDocuments()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocuments$Response(params: GetDocuments$Params, context?: HttpContext): Observable<StrictHttpResponse<DocumentItemDtoPaging>> {
    return getDocuments(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDocuments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDocuments(params: GetDocuments$Params, context?: HttpContext): Observable<DocumentItemDtoPaging> {
    return this.getDocuments$Response(params, context).pipe(
      map((r: StrictHttpResponse<DocumentItemDtoPaging>): DocumentItemDtoPaging => r.body)
    );
  }

  /** Path part for operation `uploadDocuments()` */
  static readonly UploadDocumentsPath = '/documents';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadDocuments()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadDocuments$Response(params?: UploadDocuments$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return uploadDocuments(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadDocuments$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadDocuments(params?: UploadDocuments$Params, context?: HttpContext): Observable<void> {
    return this.uploadDocuments$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `verifyDocuments()` */
  static readonly VerifyDocumentsPath = '/documents/verify';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `verifyDocuments()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  verifyDocuments$Response(params: VerifyDocuments$Params, context?: HttpContext): Observable<StrictHttpResponse<Array<DocumentVerificationResponseItem>>> {
    return verifyDocuments(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `verifyDocuments$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  verifyDocuments(params: VerifyDocuments$Params, context?: HttpContext): Observable<Array<DocumentVerificationResponseItem>> {
    return this.verifyDocuments$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<DocumentVerificationResponseItem>>): Array<DocumentVerificationResponseItem> => r.body)
    );
  }

}
